import { Alert, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormEvent, PropsWithChildren, useCallback, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import { getCurrentUser, getOtp, verifyOtp } from '@/api/auth';
import { updateBiometricConsent } from '@/api/user';

import { CountDownButton } from '@/components/Form/CountDownButton';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { OTPInput } from '@/components/Form/OTPInput';
import { Icon } from '@/components/Icon';
import { Form } from '@/components/Layout';

import { SS_BIOMETRIC_CONSENT } from '@/constants/common';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { CountrySelectionForm } from '@/pages/onboarding/country-select/types/form';

import {
  notifyUserChanged,
  setLoggedInUser,
  signOut,
} from '@/redux/auth/actions';
import { selectTerritories } from '@/redux/countries';

import { Language } from '@/types/i18n';
import { ICBResponseCode } from '@/types/user';

import { getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';

import { OTP_LENGTH } from '../constants';

type VerifyEmailOtpForm = {
  countryForm: CountrySelectionForm;
  onPendingProfileChange?: (pending: boolean) => void;
} & PropsWithChildren;

export function VerifyEmailOtpForm({
  countryForm = {
    countryCode: '',
    email: '',
    language: Language.English,
  },
  onPendingProfileChange,
  children,
}: VerifyEmailOtpForm) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { territoryByCountryCode } = useAppSelector(selectTerritories);
  const [biometricConsent, _, removeBiometricConsent] = useSessionStorage(
    SS_BIOMETRIC_CONSENT,
    '',
  );

  const { mutate: mutateBiometricConsent } = useMutation({
    mutationFn: updateBiometricConsent,
  });

  const territory = territoryByCountryCode[countryForm?.countryCode || ''];

  const [otp, setOtp] = useState('');
  const { mutate, isPending, error } = useMutation({
    mutationFn: verifyOtp,
    onSuccess: async () => {
      const { user } = await getCurrentUser(false);
      if (
        user.icbResponseCode === ICBResponseCode.ExistingCustomerProfilePending
      ) {
        onPendingProfileChange?.(true);
        dispatch(signOut());
        return;
      }
      if (user) {
        dispatch(setLoggedInUser(user));
      }
      dispatch(notifyUserChanged({ resetInit: true }));

      if (user?.biometricConsent === undefined && biometricConsent !== '') {
        mutateBiometricConsent({
          biometricConsent: biometricConsent === 'true',
        });
        removeBiometricConsent();
      }
    },
  });

  const onVerify = (e: FormEvent) => {
    e.preventDefault();

    mutate({
      payload: {
        email: countryForm.email,
        passcode: otp,
      },
      tenant: territory?.tenant,
    });
  };

  const onResend = useCallback(() => {
    if (countryForm) {
      getOtp({ email: countryForm.email, tenant: territory?.tenant });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryForm]);

  if (!countryForm) return null;

  return (
    <Form onSubmit={onVerify}>
      {children}
      <OTPInput
        length={OTP_LENGTH}
        value={otp}
        onChange={setOtp}
        disabled={isPending}
      />
      {error && (
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          {getAPIErrorMessage(error)}
        </Alert>
      )}
      <Stack direction="column" gap={2} mt={1}>
        <CountDownButton
          buttonText={t('mobileAdoption.verifyOTP.button.resendCode')}
          countDownText={t('mobileAdoption.verifyOTP.resendCodeIn')}
          onClick={onResend}
        />
        <LoadingButton
          {...getDataTestId(`verification-action-verify`)}
          disabled={otp.length < OTP_LENGTH || isPending}
          isLoading={isPending}
          type="submit"
          fullWidth
        >
          {t('common.verify')}
        </LoadingButton>
      </Stack>
    </Form>
  );
}
